import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { AlertCircle, CheckCircle, Clock, Home, Lock, Mail, User, Building2, Eye, EyeOff, ArrowLeft, AlertTriangle, HelpCircle, DollarSign, MapPin, Users, FileText } from "lucide-react";
import SubmissionSuccessDialog from "@/components/SubmissionSuccessDialog";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { formatWithTimezoneLabel, isDeadlinePassed } from "@/lib/timezone";
import temwiseLogo from "@/assets/temwise-logo.png";

const proposalSchema = z.object({
  companyName: z.string().min(2, "Company name is required"),
  contactName: z.string().min(2, "Contact name is required"),
  contactEmail: z.string().email("Valid email is required"),
  scope: z.string().min(10, "Scope is required (minimum 10 characters)"),
  deliverables: z.string().min(10, "Deliverables are required (minimum 10 characters)"),
  exemptions: z.string().optional(),
  initialBid: z.string().min(1, "Initial bid is required").refine(
    (val) => !isNaN(parseFloat(val)) && parseFloat(val) > 0,
    "Initial bid must be a positive number"
  ),
});

type ProposalFormValues = z.infer<typeof proposalSchema>;

interface RFPQuestion {
  question: string;
  explanation?: string;
  isRequired?: boolean;
}

interface PricingData {
  priceArrangement?: string;
  currency?: string;
  customCurrency?: string;
  provideEstimate?: boolean;
  estimateType?: string;
  estimatePrice?: string;
  estimatePriceMin?: string;
  estimatePriceMax?: string;
}

interface RFP {
  id: string;
  title: string;
  description: string;
  category: string | null;
  budget_range: string | null;
  submission_deadline: string;
  auction_start: string;
  auction_end: string;
  status: string;
  matter_name: string | null;
  region: string | null;
  team: string | null;
  questions_data: any;
  pricing_data?: PricingData | any;
}

interface Invitation {
  id: string;
  supplier_name: string;
  supplier_email: string;
}

// Helper function to get currency symbol
const getCurrencySymbol = (currency?: string): string => {
  const currencySymbols: Record<string, string> = {
    usd: "$",
    eur: "€",
    gbp: "£",
    jpy: "¥",
    cny: "¥",
    cad: "C$",
    aud: "A$",
    inr: "₹",
    chf: "CHF",
  };
  return currencySymbols[currency || "usd"] || "$";
};

const SupplierResponse = () => {
  const { rfpId, token } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { user, signIn, signUp, loading: authLoading } = useAuth();
  const [rfp, setRfp] = useState<RFP | null>(null);
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [existingProposal, setExistingProposal] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Auth form states
  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [authName, setAuthName] = useState("");
  const [firmName, setFirmName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSignUp, setIsSignUp] = useState(true);
  const [authSubmitting, setAuthSubmitting] = useState(false);
  const [validatingInvitation, setValidatingInvitation] = useState(true);
  const [invitationValid, setInvitationValid] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [submittedProposalData, setSubmittedProposalData] = useState<any>(null);
  const [questionResponses, setQuestionResponses] = useState<Record<number, string>>({});

  const form = useForm<ProposalFormValues>({
    resolver: zodResolver(proposalSchema),
    defaultValues: {
      companyName: "",
      contactName: "",
      contactEmail: "",
      scope: "",
      deliverables: "",
      exemptions: "",
      initialBid: "",
    },
  });

  // First, validate the invitation token and check if current session matches
  useEffect(() => {
    validateInvitationAndSession();
  }, [rfpId, token]);

  // After auth is ready and invitation is valid, fetch full data
  useEffect(() => {
    if (!authLoading && invitationValid && user) {
      // Only fetch data if the logged-in user matches the invitation email
      if (invitation && user.email?.toLowerCase() === invitation.supplier_email.toLowerCase()) {
        fetchData();
      }
    }
  }, [authLoading, invitationValid, user, invitation]);

  const validateInvitationAndSession = async () => {
    try {
      const { data: invData, error: invError } = await supabase
        .from("supplier_invitations")
        .select("id, supplier_name, supplier_email")
        .eq("invite_token", token!)
        .eq("rfp_id", rfpId!)
        .single();

      if (invError || !invData) {
        setInvitationValid(false);
        setValidatingInvitation(false);
        return;
      }

      setInvitation(invData);
      setInvitationValid(true);
      setAuthEmail(invData.supplier_email);
      setFirmName(invData.supplier_name);

      // Check if there's an existing session that doesn't match this invitation
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const sessionEmail = session.user.email?.toLowerCase();
        const invitationEmail = invData.supplier_email.toLowerCase();
        
        // If session email doesn't match invitation, sign out to prevent "sticky" session
        if (sessionEmail !== invitationEmail) {
          await supabase.auth.signOut();
        }
      }
    } catch (error) {
      setInvitationValid(false);
    } finally {
      setValidatingInvitation(false);
    }
  };

  const fetchData = async () => {
    try {
      // Fetch RFP
      const { data: rfpData, error: rfpError } = await supabase
        .from("rfps")
        .select("*")
        .eq("id", rfpId!)
        .single();

      if (rfpError) throw rfpError;
      setRfp(rfpData);

      // Check for existing proposal
      if (invitation) {
        const { data: proposalData } = await supabase
          .from("proposals")
          .select("*")
          .eq("invitation_id", invitation.id)
          .maybeSingle();

        if (proposalData) {
          setExistingProposal(proposalData);
          const details = proposalData.proposal_details ? JSON.parse(proposalData.proposal_details) : {};
          form.reset({
            companyName: proposalData.supplier_company_name,
            contactName: proposalData.contact_name,
            contactEmail: proposalData.contact_email,
            scope: details.scope || "",
            deliverables: details.deliverables || "",
            exemptions: details.exemptions || "",
            initialBid: proposalData.initial_bid_amount.toString(),
          });
        } else {
          form.reset({
            companyName: invitation.supplier_name,
            contactEmail: invitation.supplier_email,
            contactName: user?.user_metadata?.name || "",
            scope: "",
            deliverables: "",
            exemptions: "",
            initialBid: "",
          });
        }
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to load RFP details",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthSubmitting(true);
    
    try {
      if (isSignUp) {
        if (!authName.trim()) {
          throw new Error("Please enter your name");
        }
        if (!firmName.trim()) {
          throw new Error("Please enter your firm name");
        }
        if (authPassword.length < 6) {
          throw new Error("Password must be at least 6 characters");
        }

        const { error } = await signUp(authEmail, authPassword, authName);
        if (error) throw error;

        // Get the new user and assign supplier role
        const { data: { user: newUser } } = await supabase.auth.getUser();
        if (newUser) {
          await supabase.from("user_roles").insert({
            user_id: newUser.id,
            role: "supplier"
          });

          await supabase.from("supplier_profiles").insert({
            user_id: newUser.id,
            firm_name: firmName
          });
        }
        
        toast({
          title: "Account Created",
          description: "Your account has been created successfully!",
        });
      } else {
        const { error } = await signIn(authEmail, authPassword);
        if (error) throw error;
        
        toast({
          title: "Signed In",
          description: "You have been signed in successfully!",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Authentication failed. Please try again.",
        variant: "destructive"
      });
    } finally {
      setAuthSubmitting(false);
    }
  };

  const onSubmit = async (values: ProposalFormValues) => {
    if (!rfp || !invitation) return;

    // Check if auction has already started - if so, can't submit anymore
    const auctionStarted = isDeadlinePassed(rfp.auction_start);
    if (auctionStarted) {
      toast({
        title: "Submission Closed",
        description: "The auction has already started. Submissions are no longer accepted.",
        variant: "destructive",
      });
      return;
    }

    const isLateSubmission = isDeadlinePassed(rfp.submission_deadline);

    setSubmitting(true);

    try {
      const proposalDetails = JSON.stringify({
        scope: values.scope,
        deliverables: values.deliverables,
        exemptions: values.exemptions || "",
      });

      // Format question responses for storage
      const questions = rfp.questions_data?.questions || [];
      const formattedQuestionResponses = questions.map((q: RFPQuestion, index: number) => ({
        question: q.question,
        response: questionResponses[index] || "",
        isRequired: q.isRequired,
      }));

      const proposalData = {
        rfp_id: rfpId!,
        invitation_id: invitation.id,
        supplier_company_name: values.companyName,
        contact_name: values.contactName,
        contact_email: values.contactEmail,
        initial_bid_amount: parseFloat(values.initialBid),
        proposal_details: proposalDetails,
        question_responses: formattedQuestionResponses.length > 0 ? formattedQuestionResponses : null,
        status: isLateSubmission ? "LATE" as const : "SUBMITTED" as const,
        user_id: user?.id || null,
      };

      const { data, error } = await supabase
        .from("proposals")
        .insert([proposalData])
        .select()
        .single();

      if (error) throw error;

      await supabase.from("bids").insert({
        rfp_id: rfpId!,
        proposal_id: data.id,
        amount: parseFloat(values.initialBid),
        source: "INITIAL_PROPOSAL",
      });

      toast({
        title: "Success!",
        description: isLateSubmission 
          ? "Your proposal has been submitted (marked as late submission)"
          : "Your proposal has been submitted successfully",
      });

      setSubmittedProposalData(data);
      setExistingProposal(data);
      setShowSuccessDialog(true);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to submit proposal. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSuccessDialogClose = () => {
    setShowSuccessDialog(false);
  };

  const handleReturnToDashboard = () => {
    setShowSuccessDialog(false);
    navigate("/supplier/dashboard");
  };

  const handleViewProposal = () => {
    setShowSuccessDialog(false);
    navigate(`/supplier/rfp/${rfpId}/view/${token}`);
  };

  // Show loading while validating invitation
  if (validatingInvitation || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Invalid invitation
  if (!invitationValid || !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Invalid Link</h2>
            <p className="text-gray-600">This invitation link is not valid or has expired.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show sign-in form if not authenticated
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(235,88%,11%)] px-4">
        <div className="w-full max-w-md space-y-6">
          <div className="flex justify-center">
            <img src={temwiseLogo} alt="Temwise Logo" className="h-16 w-auto" />
          </div>
          
          <Card className="bg-white/10 backdrop-blur border-white/20">
            <CardHeader className="text-center">
              <CardTitle className="text-white text-xl">Law Firm Sign In</CardTitle>
              <p className="text-gray-300 text-sm mt-2">
                Please sign in to respond to this instruction/RFP
              </p>
              {invitation && (
                <Badge variant="outline" className="mt-3 text-white border-white/30">
                  Invitation for: {invitation.supplier_name}
                </Badge>
              )}
            </CardHeader>
            <CardContent>
              <Tabs value={isSignUp ? "signup" : "signin"} onValueChange={(v) => setIsSignUp(v === "signup")}>
                <TabsList className="grid w-full grid-cols-2 bg-gray-900">
                  <TabsTrigger value="signin" className="text-white data-[state=active]:bg-white data-[state=active]:text-gray-900">Sign In</TabsTrigger>
                  <TabsTrigger value="signup" className="text-white data-[state=active]:bg-white data-[state=active]:text-gray-900">Sign Up</TabsTrigger>
                </TabsList>
                
                <TabsContent value="signin">
                  <form onSubmit={handleAuth} className="space-y-4 mt-4">
                    <div>
                      <label className="block text-xs font-medium mb-1 text-white">Email</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                        <Input
                          type="email"
                          placeholder="Enter your email"
                          className="pl-10 bg-gray-900 border-gray-700 text-white"
                          value={authEmail}
                          onChange={(e) => setAuthEmail(e.target.value)}
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1 text-white">Password</label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                        <Input
                          type={showPassword ? "text" : "password"}
                          placeholder="Enter your password"
                          className="pl-10 pr-10 bg-gray-900 border-gray-700 text-white"
                          value={authPassword}
                          onChange={(e) => setAuthPassword(e.target.value)}
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                        </button>
                      </div>
                    </div>
                    <Button type="submit" className="w-full bg-white text-[#042cc4] hover:bg-gray-100" disabled={authSubmitting}>
                      {authSubmitting ? "Signing in..." : "Sign In"}
                    </Button>
                  </form>
                </TabsContent>
                
                <TabsContent value="signup">
                  <form onSubmit={handleAuth} className="space-y-4 mt-4">
                    <div>
                      <label className="block text-xs font-medium mb-1 text-white">Your Name</label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                        <Input
                          type="text"
                          placeholder="Enter your name"
                          className="pl-10 bg-gray-900 border-gray-700 text-white"
                          value={authName}
                          onChange={(e) => setAuthName(e.target.value)}
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1 text-white">Firm Name</label>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                        <Input
                          type="text"
                          placeholder="Enter your firm name"
                          className="pl-10 bg-gray-900 border-gray-700 text-white"
                          value={firmName}
                          onChange={(e) => setFirmName(e.target.value)}
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1 text-white">Email</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                        <Input
                          type="email"
                          placeholder="Enter your email"
                          className="pl-10 bg-gray-900 border-gray-700 text-white"
                          value={authEmail}
                          onChange={(e) => setAuthEmail(e.target.value)}
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1 text-white">Password</label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                        <Input
                          type={showPassword ? "text" : "password"}
                          placeholder="Create a password (min 6 characters)"
                          className="pl-10 pr-10 bg-gray-900 border-gray-700 text-white"
                          value={authPassword}
                          onChange={(e) => setAuthPassword(e.target.value)}
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                        </button>
                      </div>
                    </div>
                    <Button type="submit" className="w-full bg-white text-[#042cc4] hover:bg-gray-100" disabled={authSubmitting}>
                      {authSubmitting ? "Creating Account..." : "Create Account"}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Loading RFP data
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!rfp) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">RFP Not Found</h2>
            <p className="text-gray-600">This RFP does not exist or you don't have access.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const submissionClosed = isDeadlinePassed(rfp.submission_deadline);
  const auctionStarted = isDeadlinePassed(rfp.auction_start);
  const isBeforeDeadline = !submissionClosed;
  const isAfterDeadline = submissionClosed && !auctionStarted;

  if (existingProposal && isAfterDeadline) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <Card className="max-w-2xl mx-auto">
          <CardContent className="pt-6 text-center">
            <Clock className="h-12 w-12 text-blue-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Submission Period Closed</h2>
            <p className="text-gray-600 mb-4">
              Your proposal has been submitted. The reverse auction will start on:
            </p>
            <p className="text-lg font-semibold">{formatWithTimezoneLabel(rfp.auction_start)}</p>
            <Button
              onClick={() => navigate(`/rfp/${rfpId}/bid/${token}`)}
              className="mt-6"
            >
              View Auction Page
            </Button>
            <Button
              onClick={() => navigate("/supplier/dashboard")}
              variant="outline"
              className="mt-2 gap-2"
            >
              <Home className="h-4 w-4" />
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (existingProposal) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <Card className="max-w-2xl mx-auto">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Proposal Already Submitted</h2>
            <p className="text-gray-600 mb-4">
              You have already submitted a proposal for this RFP.
            </p>
            <Button
              onClick={() => navigate(`/rfp/${rfpId}/bid/${token}`)}
              className="mt-4"
            >
              Go to Bidding Page
            </Button>
            <Button
              onClick={() => navigate("/supplier/dashboard")}
              variant="outline"
              className="mt-2 gap-2"
            >
              <Home className="h-4 w-4" />
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex justify-between items-center">
          <Button 
            variant="outline" 
            className="gap-2"
            onClick={() => navigate("/supplier/dashboard")}
          >
            <Home className="h-4 w-4" />
            Return to Dashboard
          </Button>
        </div>
        
        {/* RFP Details Card - Pinned Reference */}
        <Card className="border-blue-200 bg-blue-50/30">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between">
              <div>
                <Badge variant="outline" className="mb-2 text-blue-600 border-blue-300">Client RFP Details</Badge>
                <CardTitle className="text-2xl">{rfp.matter_name || rfp.title}</CardTitle>
                <p className="text-gray-600 mt-2">{rfp.description}</p>
              </div>
              <Badge variant={isBeforeDeadline ? "default" : "secondary"}>
                {rfp.status.replace(/_/g, " ")}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Key Details Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {rfp.category && (
                <div className="flex items-center gap-2 p-3 bg-white rounded-lg border">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Category</p>
                    <p className="text-sm font-medium">{rfp.category}</p>
                  </div>
                </div>
              )}
              {rfp.budget_range && (
                <div className="flex items-center gap-2 p-3 bg-white rounded-lg border">
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Budget Range</p>
                    <p className="text-sm font-medium">{rfp.budget_range}</p>
                  </div>
                </div>
              )}
              {rfp.region && (
                <div className="flex items-center gap-2 p-3 bg-white rounded-lg border">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Region</p>
                    <p className="text-sm font-medium">{rfp.region.replace(/_/g, " ").toUpperCase()}</p>
                  </div>
                </div>
              )}
              {rfp.team && (
                <div className="flex items-center gap-2 p-3 bg-white rounded-lg border">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Team</p>
                    <p className="text-sm font-medium capitalize">{rfp.team.replace(/_/g, " ")}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Pricing Details */}
            {rfp.pricing_data && (
              <>
                <Separator />
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                    <p className="font-medium text-sm">Pricing Details</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {rfp.pricing_data.priceArrangement && (
                      <div className="p-3 bg-white rounded-lg border">
                        <p className="text-xs text-muted-foreground">Price Arrangement</p>
                        <p className="text-sm font-medium capitalize">
                          {rfp.pricing_data.priceArrangement.replace(/-/g, " ")}
                        </p>
                      </div>
                    )}
                    {rfp.pricing_data.currency && (
                      <div className="p-3 bg-white rounded-lg border">
                        <p className="text-xs text-muted-foreground">Currency</p>
                        <p className="text-sm font-medium uppercase">
                          {rfp.pricing_data.currency === "other" 
                            ? rfp.pricing_data.customCurrency 
                            : rfp.pricing_data.currency}
                        </p>
                      </div>
                    )}
                    {rfp.pricing_data.provideEstimate && rfp.pricing_data.estimateType === "fixed" && rfp.pricing_data.estimatePrice && (
                      <div className="p-3 bg-white rounded-lg border md:col-span-2">
                        <p className="text-xs text-muted-foreground">Estimated Price</p>
                        <p className="text-sm font-medium">
                          {getCurrencySymbol(rfp.pricing_data.currency)}{Number(rfp.pricing_data.estimatePrice).toLocaleString()}
                        </p>
                      </div>
                    )}
                    {rfp.pricing_data.provideEstimate && rfp.pricing_data.estimateType === "range" && (
                      <div className="p-3 bg-white rounded-lg border md:col-span-2">
                        <p className="text-xs text-muted-foreground">Estimated Price Range</p>
                        <p className="text-sm font-medium">
                          {getCurrencySymbol(rfp.pricing_data.currency)}{Number(rfp.pricing_data.estimatePriceMin || 0).toLocaleString()} - {getCurrencySymbol(rfp.pricing_data.currency)}{Number(rfp.pricing_data.estimatePriceMax || 0).toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            <Separator />

            {/* Timeline */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-3 bg-white rounded-lg border">
                <p className="text-xs text-muted-foreground">Submission Deadline</p>
                <p className="text-sm font-medium">{formatWithTimezoneLabel(rfp.submission_deadline)}</p>
              </div>
              <div className="p-3 bg-white rounded-lg border">
                <p className="text-xs text-muted-foreground">Auction Start</p>
                <p className="text-sm font-medium">{formatWithTimezoneLabel(rfp.auction_start)}</p>
              </div>
              <div className="p-3 bg-white rounded-lg border">
                <p className="text-xs text-muted-foreground">Auction End</p>
                <p className="text-sm font-medium">{formatWithTimezoneLabel(rfp.auction_end)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Late Submission Warning Banner */}
        {!isBeforeDeadline && !auctionStarted && (
          <Card className="border-amber-500 bg-amber-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <AlertTriangle className="h-6 w-6 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-amber-800">Late Submission Warning</h3>
                  <p className="text-amber-700 text-sm mt-1">
                    Note: The submission deadline has passed. This proposal will be marked as a late submission.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Block submission only if auction has started */}
        {auctionStarted ? (
          <Card>
            <CardContent className="pt-6 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Auction Already Started</h3>
              <p className="text-gray-600">
                The auction has already started. Submissions are no longer accepted.
              </p>
              <Button
                onClick={() => navigate(`/rfp/${rfpId}/bid/${token}`)}
                className="mt-4"
              >
                View Auction
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Submit Your Proposal</CardTitle>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="companyName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Company Name *</FormLabel>
                        <FormControl>
                          <Input placeholder="Your company name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="contactName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Contact Name *</FormLabel>
                          <FormControl>
                            <Input placeholder="Your name" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="contactEmail"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Contact Email *</FormLabel>
                          <FormControl>
                            <Input type="email" placeholder="your@email.com" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="initialBid"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Initial Bid Amount ($) *</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="Enter your bid amount"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="scope"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Scope of Work *</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Describe the scope of work you will deliver..."
                            className="min-h-[100px]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="deliverables"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Deliverables *</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="List all deliverables you will provide..."
                            className="min-h-[100px]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="exemptions"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Exemptions / Assumptions / Exclusions</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="List any exemptions, assumptions, or exclusions (optional)..."
                            className="min-h-[100px]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Client Questions Section */}
                  {rfp.questions_data?.questions && rfp.questions_data.questions.length > 0 && (
                    <div className="space-y-4 pt-4 border-t">
                      <div className="flex items-center gap-2">
                        <HelpCircle className="h-5 w-5 text-blue-600" />
                        <h3 className="font-semibold text-lg">Client Questions</h3>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Please answer the following questions from the client.
                      </p>
                      
                      {rfp.questions_data.questions.map((question: RFPQuestion, index: number) => (
                        <div key={index} className="p-4 border rounded-lg bg-gray-50 space-y-3">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="font-medium">
                                {index + 1}. {question.question}
                                {question.isRequired && <span className="text-red-500 ml-1">*</span>}
                              </p>
                              {question.explanation && (
                                <p className="text-sm text-muted-foreground mt-1">{question.explanation}</p>
                              )}
                            </div>
                            {question.isRequired && (
                              <Badge variant="outline" className="shrink-0 text-xs">Required</Badge>
                            )}
                          </div>
                          <Textarea
                            placeholder="Enter your response..."
                            className="min-h-[80px] bg-white"
                            value={questionResponses[index] || ""}
                            onChange={(e) => setQuestionResponses(prev => ({
                              ...prev,
                              [index]: e.target.value
                            }))}
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  <Button type="submit" className="w-full" disabled={submitting}>
                    {submitting ? "Submitting..." : "Submit Proposal"}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Success Dialog */}
      <SubmissionSuccessDialog
        open={showSuccessDialog}
        onClose={handleSuccessDialogClose}
        onReturnToDashboard={handleReturnToDashboard}
        onViewProposal={handleViewProposal}
      />
    </div>
  );
};

export default SupplierResponse;
